import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Shield, User as UserIcon, ShieldAlert, Trash2, UserCog, Search, Edit2, Upload, Check, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { logger } from '@/lib/logger';
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type UserWithRole = {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: string;
  role_id: string;
};

type Team = {
  id: string;
  name: string;
};

export default function UserManagement() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithRole[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserWithRole | null>(null);
  const [editingUser, setEditingUser] = useState<UserWithRole | null>(null);
  const [editName, setEditName] = useState("");
  const [editHeadshotFile, setEditHeadshotFile] = useState<File | null>(null);
  const [editHeadshotPreview, setEditHeadshotPreview] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const { toast } = useToast();
  const { startImpersonation, getEffectiveUserId, isImpersonating, impersonatedUser } = useImpersonation();
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminStatus();
  }, [isImpersonating, impersonatedUser?.id]);

  const checkAdminStatus = async () => {
    try {
      // Always check the real authenticated user for admin status (not impersonated)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        logger.error('[UserManagement] No authenticated user found');
        return;
      }

      logger.debug('[UserManagement] Checking admin status for user:', user.id, user.email);

      // Allow system admin (admin@northreports.com) or users with admin role
      if (user.email === 'admin@northreports.com') {
        logger.debug('[UserManagement] System admin detected');
        setIsAdmin(true);
        setIsSystemAdmin(true);
        await loadUsers();
        return;
      }

      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .maybeSingle();

      if (roleError) {
        logger.error('[UserManagement] Error checking admin role:', roleError);
      } else {
        logger.debug('[UserManagement] Role check result:', roleData ? `Role: ${roleData.role}` : 'No role found');
      }

      if (roleData?.role === "admin") {
        logger.debug('[UserManagement] Admin role confirmed');
        setIsAdmin(true);
        setIsSystemAdmin(false);
        await loadUsers();
      } else {
        logger.debug('[UserManagement] Not an admin');
      }
    } catch (error: any) {
      logger.error('[UserManagement] Error checking admin status:', error);
      logger.error("Error checking admin status:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // Get the effective user ID (impersonated user if impersonating, otherwise authenticated user)
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) return;

      // Check if the real authenticated user is system admin
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const isSystemAdmin = authUser?.email === 'admin@northreports.com';

      let profiles;
      
      if (isImpersonating) {
        // When impersonating, show only users from the impersonated user's team
        const { data: impersonatedProfile, error: profileError } = await supabase
          .from("profiles")
          .select("team_id")
          .eq("user_id", effectiveUserId)
          .single();

        if (profileError) throw profileError;

        if (!impersonatedProfile?.team_id) {
          setUsers([]);
          setFilteredUsers([]);
          return;
        }

        // Get only users from the impersonated user's team (excluding system admin)
        const { data, error: profilesError } = await supabase
          .from("profiles")
          .select("user_id, full_name, avatar_url, email, team_id")
          .eq("team_id", impersonatedProfile.team_id)
          .neq("email", "admin@northreports.com");

        if (profilesError) throw profilesError;
        profiles = data;
      } else if (isSystemAdmin) {
        // System admin sees all users when not impersonating
        const { data, error: profilesError } = await supabase
          .from("profiles")
          .select("user_id, full_name, avatar_url, email, team_id");
        
        if (profilesError) throw profilesError;
        profiles = data;
      } else {
        // Regular team admin - get their team_id first
        const { data: myProfile, error: myProfileError } = await supabase
          .from("profiles")
          .select("team_id")
          .eq("user_id", effectiveUserId)
          .single();

        if (myProfileError) throw myProfileError;

        // Get only users from the same team (excluding system admin)
        const { data, error: profilesError } = await supabase
          .from("profiles")
          .select("user_id, full_name, avatar_url, email, team_id")
          .eq("team_id", myProfile.team_id)
          .neq("email", "admin@northreports.com");

        if (profilesError) throw profilesError;
        profiles = data;
      }

      logger.log("Loaded profiles:", profiles);

      // Get all user roles
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("id, user_id, role");

      if (rolesError) throw rolesError;
      logger.log("Loaded roles:", roles);

      // Combine the data
      const combinedUsers: UserWithRole[] = profiles.map(profile => {
        const userRole = roles.find(r => r.user_id === profile.user_id);

        return {
          id: profile.user_id,
          email: profile.email || "No email",
          full_name: profile.full_name,
          avatar_url: profile.avatar_url,
          role: userRole?.role || "agent",
          role_id: userRole?.id || "",
        };
      });

      logger.log("Combined users:", combinedUsers);
      setUsers(combinedUsers);
      setFilteredUsers(combinedUsers);
    } catch (error: any) {
      logger.error("Error loading users:", error);
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive",
      });
    }
  };

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(user =>
      user.full_name?.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const handleImpersonate = async (user: UserWithRole) => {
    try {
      setImpersonating(user.id);
      await startImpersonation(user.id, user.full_name || user.email, user.email);
      toast({
        title: "Impersonation started",
        description: `Now viewing as ${user.full_name || user.email}`,
      });
      navigate('/dashboard');
    } catch (error: any) {
      logger.error("Error starting impersonation:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to start impersonation",
        variant: "destructive",
      });
    } finally {
      setImpersonating(null);
    }
  };

  const toggleAdminRole = async (userId: string, currentRole: string, roleId: string) => {
    try {
      setUpdatingRole(userId);
      const newRole = currentRole === "admin" ? "agent" : "admin";

      // Use database function to set role - this bypasses RLS issues
      const { error: rpcError } = await supabase.rpc('set_user_role', {
        target_user_id: userId,
        new_role: newRole
      });

      if (rpcError) {
        logger.error("RPC error:", rpcError);
        throw rpcError;
      }

      toast({
        title: "Success",
        description: `User role updated to ${newRole}`,
      });

      await loadUsers();
    } catch (error: any) {
      logger.error("Error updating role:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update user role",
        variant: "destructive",
      });
    } finally {
      setUpdatingRole(null);
    }
  };


  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeletingUser(userToDelete.id);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      const { error } = await supabase.functions.invoke('delete-user', {
        body: { userId: userToDelete.id },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "User deleted successfully",
      });

      setUserToDelete(null);
      await loadUsers();
    } catch (error: any) {
      logger.error("Error deleting user:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
    } finally {
      setDeletingUser(null);
    }
  };

  const startEdit = (user: UserWithRole) => {
    setEditingUser(user);
    setEditName(user.full_name || "");
    setEditHeadshotPreview(user.avatar_url);
    setEditHeadshotFile(null);
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditName("");
    setEditHeadshotFile(null);
    setEditHeadshotPreview(null);
  };

  const handleHeadshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setEditHeadshotFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditHeadshotPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadHeadshot = async (file: File, userId: string): Promise<string> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${userId}/avatar-${Date.now()}.${fileExt}`;

    logger.debug('[UserManagement] Uploading headshot:', {
      fileName,
      userId,
      fileSize: file.size,
      fileType: file.type,
    });

    const { data, error: uploadError } = await supabase.storage
      .from("logos")
      .upload(fileName, file, {
        contentType: file.type,
        upsert: true,
      });

    if (uploadError) {
      logger.error('[UserManagement] Upload error details:', {
        error: uploadError,
        message: uploadError.message,
        statusCode: uploadError.statusCode,
        errorDetails: JSON.stringify(uploadError, null, 2),
      });
      throw uploadError;
    }

    logger.debug('[UserManagement] Upload successful:', data);

    const { data: { publicUrl } } = supabase.storage
      .from("logos")
      .getPublicUrl(fileName);

    return publicUrl;
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    setSavingEdit(true);
    try {
      let headshotUrl: string | null = editingUser.avatar_url;

      // Upload headshot if provided
      if (editHeadshotFile) {
        headshotUrl = await uploadHeadshot(editHeadshotFile, editingUser.id);
      }

      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: editName.trim() || null,
          avatar_url: headshotUrl,
        })
        .eq('user_id', editingUser.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "User updated successfully",
      });

      cancelEdit();
      await loadUsers();
    } catch (error: any) {
      logger.error('Error updating user:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update user",
        variant: "destructive",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <ShieldAlert className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
          <p className="text-muted-foreground text-center">
            You need administrator privileges to access user management.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Management</CardTitle>
        <CardDescription>
          Manage user roles and permissions. Team members are added via invitation.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {searchQuery && (
            <p className="text-sm text-muted-foreground mt-2">
              Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right w-[350px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="select-text cursor-text">
                  <div className="flex items-center gap-3">
                    <Avatar className="select-none">
                      <AvatarImage src={user.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        <UserIcon className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium">
                      {user.full_name || "No name"}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground select-text cursor-text">
                  {user.email}
                </TableCell>
                <TableCell>
                  <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                    {user.role === "admin" ? (
                      <Shield className="h-3 w-3 mr-1" />
                    ) : (
                      <UserIcon className="h-3 w-3 mr-1" />
                    )}
                    {user.role}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(user)}
                      disabled={updatingRole === user.id || deletingUser === user.id || impersonating === user.id || savingEdit}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    {isSystemAdmin && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleImpersonate(user)}
                        disabled={impersonating === user.id || updatingRole === user.id || deletingUser === user.id || savingEdit}
                      >
                        {impersonating === user.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <UserCog className="h-4 w-4 mr-1" />
                            Impersonate
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleAdminRole(user.id, user.role, user.role_id)}
                      disabled={updatingRole === user.id || deletingUser === user.id || impersonating === user.id || savingEdit}
                    >
                      {updatingRole === user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : user.role === "admin" ? (
                        "Remove Admin"
                      ) : (
                        "Make Admin"
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setUserToDelete(user)}
                      disabled={updatingRole === user.id || deletingUser === user.id || impersonating === user.id || savingEdit}
                    >
                      {deletingUser === user.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!editingUser} onOpenChange={(open) => !open && cancelEdit()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user name and headshot
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter user name"
              />
            </div>
            <div className="space-y-2">
              <Label>Headshot (Optional)</Label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-full border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden">
                  {editHeadshotPreview ? (
                    <img
                      src={editHeadshotPreview}
                      alt="Headshot preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-8 h-8 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleHeadshotChange}
                    className="hidden"
                    id="edit-headshot-upload"
                  />
                  <Label htmlFor="edit-headshot-upload">
                    <Button variant="outline" size="sm" asChild>
                      <span className="cursor-pointer">
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Headshot
                      </span>
                    </Button>
                  </Label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelEdit} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.full_name || userToDelete?.email}</strong>? 
              This action cannot be undone. All user data, including their profile and associated records, will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingUser}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={!!deletingUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingUser ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete User"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
